import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { showToast } from "./PopupMessage.jsx";
import {
  Box,
  Typography,
  Container,
  Card,
  CardMedia,
  Button,
  CircularProgress,
  Modal,
  IconButton,
  TextField,
  MenuItem,
} from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import { motion } from "framer-motion";
import DOMPurify from "dompurify";
import Navber from "../Components/Navber";
import api from "../Api"; // axios instance

const ActivityDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);

  // Booking states
  const [openBooking, setOpenBooking] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [seats, setSeats] = useState(1);
  const [totalAmount, setTotalAmount] = useState(0);

  const accessToken = localStorage.getItem("accessToken");

  useEffect(() => {
    fetchActivityDetails();
  }, [id]);

  const fetchActivityDetails = async () => {
    try {
      const res = await api.get(`/activities/${id}`);
      setActivity(res.data.data);
    } catch (err) {
      console.error("Error fetching activity:", err);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Open booking modal
  const handleOpenBooking = (destination) => {
    setSelectedDestination(destination);
    setSeats(1);
    setTotalAmount(destination.price);
    setOpenBooking(true);
  };

  // 🔹 Close modal
  const handleCloseBooking = () => {
    setOpenBooking(false);
  };

  // 🔹 Change number of seats
  const handleSeatChange = (type) => {
    if (!selectedDestination) return;

    if (type === "increase") {
      const newSeats = seats + 1;
      setSeats(newSeats);
      setTotalAmount(newSeats * selectedDestination.price);
    } else if (type === "decrease" && seats > 1) {
      const newSeats = seats - 1;
      setSeats(newSeats);
      setTotalAmount(newSeats * selectedDestination.price);
    }
  };

  // 🔹 Razorpay Payment Integration
  const handlePayment = async () => {
    if (!selectedDestination) return;

    try {
      const payload = {
        destination_id: selectedDestination.destination_id,
        seats,
        total_amount: totalAmount,
        currency: "INR",
      };

      const order = await api.post(
        `/activities/${activity?.id}/book`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      console.log("Booking created:", order);

      const options = {
        key: order?.data?.data?.razorpay_key, // Razorpay key
        amount: order?.data?.data?.amount,
        currency: order?.data?.data?.currency,
        name: "Jayraj Group Activity",
        description: order?.data?.data?.description,
        order_id: order?.data?.data?.razorpay_order_id,
        handler: async (response) => {
          const verifyPayload = {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          };
          console.log("verifyPayload: ",verifyPayload);

          try {
            const verifyRes = await api.post(
              `/activities/bookings/${order?.data?.data?.booking_id}/verify`,
              verifyPayload,
              {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${accessToken}`,
                },
              }
            );

            if (verifyRes?.status === 200) {
              showToast("Payment Successfully Done!", "success");
              navigate("/profile/activity-info");
            } else {
              showToast("Payment verification failed", "error");
            }
          } catch (err) {
            console.error("Verification error:", err);
            showToast("Error verifying payment", "error");
          }
        },
        prefill: {
          name: "Akash Shaikh",
          email: "akash@example.com",
          contact: "9999999999",
        },
        theme: {
          color: "#3399cc",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
      setOpenBooking(false);
    } catch (err) {
      console.error("Payment initiation error:", err);
      showToast("Payment initialization failed", "error");
    }
  };

  // 🔹 Loading state
  if (loading) {
    return (
      <Box
        sx={{
          height: "80vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!activity) {
    return (
      <Typography variant="h6" sx={{ textAlign: "center", mt: 5 }}>
        Activity not found.
      </Typography>
    );
  }

  const sanitizedDescription = DOMPurify.sanitize(activity.description);

  return (
    <>
           <div style={{ backgroundColor: "#f0f0f0" }}>
        <Navber />
      </div>

      <Container sx={{ py: 6 }}>
        {/* ✅ Layout Section */}
        <Box
          component={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 4,
            alignItems: "stretch",
            justifyContent: "center",
          }}
        >
          {/* 🖼️ Image Section */}
          <Card
            sx={{
              width: { xs: "100%", md: "50%" },
              boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
              borderRadius: "15px",
              overflow: "hidden",
              display: "flex",
              alignItems: "stretch",
            }}
          >
            <CardMedia
              component="img"
              image={activity.image}
              alt={activity.name}
              sx={{
                objectFit: "cover",
                width: "100%",
                height: "100%",
              }}
            />
          </Card>

          {/* 📋 Details Section */}
          <Card
            sx={{
              width: { xs: "100%", md: "50%" },
              p: 3,
              borderRadius: "15px",
              boxShadow: "0 2px 15px rgba(0,0,0,0.1)",
              background: "#fff",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
            component={motion.div}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: "bold", mb: 2, color: "#1976d2" }}
              >
                {activity.name}
              </Typography>

              <div
                dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
                className="description_activity"
              />

              <Typography variant="h6" sx={{ mt: 2, fontWeight: "bold" }}>
                Available Destinations:
              </Typography>

              <Box sx={{ mt: 1 }}>
                {activity.destinations.map((d) => (
                  <Box
                    key={d.id}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1,
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    <Typography>{d.destination_name}</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography sx={{ fontWeight: "bold", color: "#1976d2" }}>
                        ₹{d.price.toLocaleString()}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ borderRadius: "20px", textTransform: "none" }}
                        onClick={() => handleOpenBooking(d)}
                      >
                        Book
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Card>
        </Box>
      </Container>

      {/* 🧾 Booking Overlay Modal */}
      <Modal
        open={openBooking}
        onClose={handleCloseBooking}
        aria-labelledby="booking-modal-title"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: "90%",
            maxWidth: 500,
            backgroundColor: "#fff",
            borderRadius: "16px",
            boxShadow: 24,
            p: 4,
            position: "relative",
          }}
        >
          <Typography
            id="booking-modal-title"
            variant="h5"
            sx={{ mb: 3, fontWeight: "bold", color: "#1976d2" }}
          >
            Book Your Seats
          </Typography>

          {/* Destination */}
          <TextField
            select
            fullWidth
            label="Destination"
            value={selectedDestination?.id || ""}
            sx={{ mb: 3 }}
          >
            {selectedDestination && (
              <MenuItem value={selectedDestination.id}>
                {selectedDestination.destination_name}
              </MenuItem>
            )}
          </TextField>

          {/* Seats */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 3,
            }}
          >
            <Typography>Number of Seats:</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton color="primary" onClick={() => handleSeatChange("decrease")}>
                <Remove />
              </IconButton>
              <Typography sx={{ fontSize: "1.2rem", fontWeight: "bold" }}>
                {seats}
              </Typography>
              <IconButton color="primary" onClick={() => handleSeatChange("increase")}>
                <Add />
              </IconButton>
            </Box>
          </Box>

          {/* Total */}
          <Typography
            variant="h6"
            sx={{ mb: 3, color: "#1976d2", fontWeight: "bold" }}
          >
            Total Amount: ₹{totalAmount.toLocaleString()}
          </Typography>

          {/* Buttons */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button variant="outlined" color="error" onClick={handleCloseBooking}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={handlePayment}>
              Book Now
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ActivityDetails;
